// mantine
import { Button, Textarea, TextInput, UnstyledButton } from '@mantine/core';
// components
// utils
import { useFormik } from 'formik';
import { processFormData } from '@newo/toolkit/bff';
import { TAgentWithFlows, TAgentData } from '@newo/api/gateway';
import validationSchema from '@/pages/Agents/components/AgentForm/AgentForm.schema.ts';
import PersonaSelect from '@/pages/Agents/components/PersonaSelect';
import {
    useCreateAgentMutation,
    useUpdateAgentMutation
} from '@/services/designer/agents/agents.queries.ts';

type AgentFormProps = {
    agent?: TAgentWithFlows;
    projectId?: string;
    onClose?: () => void;
    onSubmit?: () => void;
};

const AgentForm = ({ agent, projectId, onClose, onSubmit }: AgentFormProps) => {
    const { mutate: createAgent } = useCreateAgentMutation();
    const { mutate: updateAgent } = useUpdateAgentMutation();

    const initialValues: TAgentData = {
        idn: agent?.idn ?? '',
        title: agent?.title ?? '',
        description: agent?.description ?? '',
        persona_id: agent?.persona?.id ?? ''
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            if (agent) {
                const data = processFormData(formData, true);
                updateAgent(
                    { agentId: agent.id, data },
                    {
                        onSuccess() {
                            onSubmit?.();
                            onClose?.();
                        },
                        onSettled() {
                            formik.setSubmitting(false);
                        }
                    }
                );
                return;
            }
            const data = processFormData(formData, true);
            if (!projectId) {
                throw new Error('Project ID is required');
            }
            createAgent(
                { projectId, data },
                {
                    onSuccess() {
                        onSubmit?.();
                        onClose?.();
                    },
                    onSettled() {
                        formik.setSubmitting(false);
                    }
                }
            );
        }
    });

    const handleCancel = () => {
        onClose?.();
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                description="Unique Identifying Name"
                error={
                    formik.touched.idn && formik.values.idn
                        ? formik.errors.idn
                        : undefined
                }
                label="Idn"
                name="idn"
                placeholder="Idn"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <TextInput
                description="Agent title"
                error={
                    formik.touched.title && formik.values.title
                        ? formik.errors.title
                        : undefined
                }
                label="Title"
                name="title"
                placeholder="Agent title"
                value={formik.values.title ?? ''}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Textarea
                description="Agent description"
                error={
                    formik.touched.description && formik.values.description
                        ? formik.errors.description
                        : undefined
                }
                label="Description"
                name="description"
                placeholder="Agent description"
                rows={4}
                value={formik.values.description ?? ''}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <PersonaSelect
                label="Personas"
                persona={agent?.persona}
                placeholder="Find Personas"
                onSelect={(personaId) => {
                    formik.setFieldValue('persona_id', personaId ?? '');
                }}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {agent ? 'Update' : 'Create'}
                </Button>
            </div>
        </form>
    );
};

export default AgentForm;
